import React from 'react'
// constants
import { Forms } from 'constants/forms'
import { DATE_FORMAT_VALUES } from 'constants/objects'
// components
import Lookup from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/Lookup'
import ScheduledField from 'components/PageComponents/Risks/Edit/components/ScheduledField'
import ReduxLocationPicker from 'components/FormElements/ReduxForm/ReduxLocationPicker'
import { ReduxDatePicker, ReduxDateTimePicker, ReduxInput, ReduxSelect, ReduxTimePicker } from 'components/FormElements'
// types
import { FieldType, IField, IFieldFilter, IResponseTypesSet, SystemProcessConditionAction } from 'types/graphql'
// utils
import moment from 'moment/moment'
import { getFieldValues } from 'utils/form'
import { getValueOptions } from 'utils/systemProcess'

export const getFieldValue = (field: IField, value: any) => {
  if (field.fieldType === FieldType.Switch) {
    return value.value
  }

  if (field.fieldType === FieldType.Tag) {
    return { title: value }
  }

  if (field.fieldType === FieldType.Date) {
    return new Date(value).toISOString()
  }

  if (field.fieldType === FieldType.PickList && field.responseType) {
    return { value: value.id }
  }

  return getFieldValues([field], { [field.name]: value })[0].value
}

export const generateFieldFilter = (item, fields) => {
  const field = fields.find((field) => field.id === item.id)

  const filter = {
    action: item.action,
    fieldType: item.fieldType,
    originalValue: item.value,
    value: getFieldValue(field, item.value)
  }

  // A custom column has no field on the row itself, so the filter carries the source field and the
  // form it sits on, and lets the backend resolve it above the row.
  if (field?.fromCustomColumn) {
    return { ...filter, id: field.sourceFieldId, riskTemplateId: field.sourceTemplateId, fromCustomColumn: true }
  }

  return { ...filter, id: item.id, riskTemplateId: item.riskTemplateId }
}

export const generateFieldValueFromFilter = (filter, fields) => {
  const field = filter.fromCustomColumn
    ? fields?.find(
        (item) =>
          item.fromCustomColumn && item.sourceFieldId === filter.id && item.sourceTemplateId === filter.riskTemplateId
      )
    : fields?.find((item) => item.id === filter.id)

  return {
    id: field?.id ?? filter.id,
    action: filter.action,
    fieldType: filter.fieldType,
    value: filter.originalValue,
    name: field?.label,
    riskTemplateId: filter.riskTemplateId
  }
}

export const getValueField = ({
  name,
  field,
  scheduled,
  lookupProps,
  dueDateNowOnly,
  responseTypeSets
}: {
  name: string
  field: IField
  scheduled?: boolean
  lookupProps?: React.ComponentProps<typeof Lookup>
  dueDateNowOnly?: boolean
  responseTypeSets?: IResponseTypesSet[]
}) => {
  if (!field) {
    return <ReduxInput name={name} disabled />
  }

  if (field.fieldType === FieldType.Date) {
    const format = field.settings.format

    const pickerProps = {
      name
    }

    if (scheduled) {
      return (
        <ScheduledField
          field={pickerProps.name}
          dueDateNowOnly={dueDateNowOnly}
          form={Forms.SYSTEM_PROCESS_CREATE_FORM}
        />
      )
    }

    return (
      <>
        {(!format || format === DATE_FORMAT_VALUES.DATE) && <ReduxDatePicker {...pickerProps} />}
        {format === DATE_FORMAT_VALUES.TIME && <ReduxTimePicker {...pickerProps} />}
        {format === DATE_FORMAT_VALUES.DATE_TIME && <ReduxDateTimePicker {...pickerProps} />}
      </>
    )
  }

  if (field.fieldType === FieldType.Number) {
    return <ReduxInput name={name} type='number' />
  }

  if (field.fieldType === FieldType.Lookup) {
    return <Lookup name={name} field={{ ...field, label: null }} {...lookupProps} />
  }

  if (field.fieldType === FieldType.Address) {
    return <ReduxLocationPicker name={name} />
  }

  if (field.fieldType === FieldType.PickList || field.fieldType === FieldType.Switch) {
    const options = field.responseType
      ? responseTypeSets?.find((item) => item.id == field.responseTypeId)?.options || []
      : field.options

    return (
      <ReduxSelect
        name={name}
        isMultiple={scheduled && field.multiple}
        options={getValueOptions({ ...field, fieldOptions: options })}
      />
    )
  }

  return <ReduxInput name={name} />
}

export const FILTER_ACTION_OPTIONS = [
  {
    label: 'Contains ',
    value: SystemProcessConditionAction.Contains
  },
  {
    label: 'Is',
    value: SystemProcessConditionAction.Is
  },
  {
    label: 'Is not',
    value: SystemProcessConditionAction.IsNot
  },
  {
    label: 'Less than',
    value: SystemProcessConditionAction.LessThan
  },
  {
    label: 'Greater than or equal to',
    value: SystemProcessConditionAction.GreaterThan
  }
]

const getConditionOptions = (actions: SystemProcessConditionAction[]) =>
  FILTER_ACTION_OPTIONS.filter(({ value }) => actions.includes(value))

export const getOptions = (field: IField) => {
  const { Contains, Is, IsNot, LessThan, GreaterThan } = SystemProcessConditionAction

  if (field.fieldType === FieldType.Date) {
    return getConditionOptions([LessThan, GreaterThan])
  }

  if (field.fieldType === FieldType.Text) {
    return getConditionOptions([Contains, Is, IsNot])
  }

  if (field.fieldType === FieldType.Number) {
    return getConditionOptions([Is, IsNot, LessThan, GreaterThan])
  }

  if (
    field.fieldType === FieldType.Lookup ||
    field.fieldType === FieldType.Address ||
    field.fieldType === FieldType.PickList ||
    field.fieldType === FieldType.Switch ||
    field.fieldType === FieldType.Tag
  ) {
    return getConditionOptions([Is, IsNot])
  }
}

export const getFilterValue = (filter: IFieldFilter) => {
  if (filter.fieldType === FieldType.Date) {
    return moment(filter.value).format('DD MMM Y')
  }

  if (filter.fieldType === FieldType.Address) {
    return filter.value?.value?.address
  }

  return filter.value?.label || filter.value
}
